import { BlockNoteEditor, PartialBlock } from '@blocknote/core';
import { en as enDictionary } from '@blocknote/core/locales';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/shadcn';
import * as React from 'react';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/shadcn/style.css';

import { cn } from '@heartplus/ui';

export interface BlockNoteEditorProps {
  /**
   * Initial content for the editor
   */
  initialContent?: PartialBlock[];
  /**
   * Callback when content changes
   */
  onChange?: (blocks: PartialBlock[]) => void;
  /**
   * Whether the editor is editable
   */
  editable?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Text shown while the editor is empty, replacing BlockNote's built-in
   * prompt. Read once when the editor is created, so a later change to it
   * does not reach an already-mounted editor.
   */
  placeholder?: string;
  /**
   * Force a specific color scheme. Leave undefined to inherit from the
   * host document (the `<html>.dark` class toggled by `ThemeProvider`).
   * Without this, `BlockNoteView` falls back to the browser's
   * `prefers-color-scheme`, which produces white-on-white editor text in
   * a light-mode app on dark-mode OS.
   */
  theme?: 'light' | 'dark';
  /**
   * When true, disables BlockNote's built-in UI layers — the floating
   * formatting toolbar that appears on text selection, the block side
   * menu (drag handle + "add block" button) that follows the hovered
   * block, the slash menu, and the link toolbar. Use this when the
   * consumer already renders its own persistent toolbar and doesn't
   * want the default overlays fighting for the user's attention.
   */
  hideDefaultUI?: boolean;
}

/**
 * Observes `<html>.dark` and returns 'light' | 'dark' in sync with the
 * global theme toggled by the shared `ThemeProvider`. We watch the DOM
 * directly because this library is consumed from single-spa parcels
 * (e.g. external-monitors-frontend), where the host's React context tree
 * is out of reach but the `<html>` class is always visible.
 */
function useHostColorScheme(): 'light' | 'dark' {
  const [scheme, setScheme] = React.useState<'light' | 'dark'>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light';
  });

  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const read = () =>
      setScheme(root.classList.contains('dark') ? 'dark' : 'light');
    read();
    const observer = new MutationObserver(read);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return scheme;
}

/**
 * BlockNote Rich Text Editor Component
 *
 * A block-based rich text editor similar to Notion, built on ProseMirror.
 * Supports rich formatting, lists, code blocks, and more.
 */
export const BlockNoteEditorComponent = React.forwardRef<
  BlockNoteEditor,
  BlockNoteEditorProps
>(
  (
    {
      initialContent,
      onChange,
      editable = true,
      className,
      placeholder,
      theme,
      hideDefaultUI = false,
    },
    ref,
  ) => {
    // BlockNote's built-in prompt advertises its slash menu ("type '/' for
    // commands"), which is wrong for any editor that turns that menu off, so
    // a caller-supplied placeholder replaces both the empty-document and
    // empty-block prompts.
    const dictionary = React.useMemo(
      () =>
        placeholder
          ? {
              ...enDictionary,
              placeholders: {
                ...enDictionary.placeholders,
                default: placeholder,
                emptyDocument: placeholder,
              },
            }
          : undefined,
      [placeholder],
    );

    const editor = useCreateBlockNote({
      initialContent: initialContent,
      ...(dictionary ? { dictionary } : {}),
    });

    const hostScheme = useHostColorScheme();
    const resolvedTheme = theme ?? hostScheme;

    const handleChange = React.useCallback(
      (editor: BlockNoteEditor) => {
        const blocks = editor.document;
        onChange?.(blocks);
      },
      [onChange],
    );

    React.useImperativeHandle(ref, () => editor, [editor]);

    // When the consumer opts out of the default UI we forward `false`
    // for each overlay slot. BlockNoteView reads these via its default
    // children and omits the corresponding controller + portal, so they
    // stop mounting entirely — not just hidden via CSS, which would
    // still leave the hover listeners wired up.
    const defaultUIProps = hideDefaultUI
      ? ({
          formattingToolbar: false,
          sideMenu: false,
          slashMenu: false,
          linkToolbar: false,
        } as const)
      : undefined;

    return (
      <div className={cn('blocknote-editor-wrapper', className)}>
        <BlockNoteView
          editor={editor}
          editable={editable}
          onChange={handleChange}
          theme={resolvedTheme}
          {...defaultUIProps}
        />
      </div>
    );
  },
);

BlockNoteEditorComponent.displayName = 'BlockNoteEditor';

export { BlockNoteEditorComponent as BlockNoteEditor };
